import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSearch, useLocation } from 'wouter';
import { Hero } from '../components/ui/primitives';
import { ProductCard } from '../components/ui/ProductGrid';
import { EmptyState, ErrorState, Skeleton } from '../components/ui/primitives';
import { fetchProducts } from '../lib/api';
import { usePageMeta } from '../lib/seo';
import { Link } from 'wouter';

/**
 * Catalogue. Baseline §3 — navy hero (588px, 64px 0) then filter bar + product grid.
 *
 * Production hardcoded 14 products and 7 filters in the bundle. Both now come from
 * the API (A-4, A-5). Filters are real toggle buttons reflected in the URL (R-9),
 * so a filtered catalogue can be linked and the back button works.
 */

export function Catalogue() {
  const search = useSearch();
  const [, navigate] = useLocation();

  const active = useMemo(() => new URLSearchParams(search).get('category') ?? 'all', [search]);

  usePageMeta({
    title: 'Product Catalogue',
    description:
      'Browse Mopani Suppliers’ range of cleaning chemicals, paper products, janitorial supplies, hygiene products and PPE for schools and institutions in the Mopani District.',
    path: '/catalogue',
  });

  const query = useQuery({
    queryKey: ['products', 'all'],
    queryFn: () => fetchProducts(),
    staleTime: 5 * 60 * 1000,
  });

  const setCategory = (slug: string) => {
    navigate(slug === 'all' ? '/catalogue' : `/catalogue?category=${encodeURIComponent(slug)}`);
  };

  const products = query.data?.data ?? [];
  const categories = query.data?.categories ?? [];
  const visible =
    active === 'all' ? products : products.filter((p) => (p.category ?? '').toLowerCase() === active.toLowerCase());

  return (
    <>
      <Hero
        title="Product Catalogue"
        subtitle="Professional-grade cleaning and hygiene supplies. All items quoted to order — no retail markup."
        image="/assets/hero-catalogue.jpg"
      />

      <section className="section bg-white">
        <div className="container-content">
          {/* Filter bar — preserved shape (P-11), now driven by real categories. */}
          {categories.length > 0 && (
            <div className="mb-10 flex flex-wrap gap-3" role="group" aria-label="Filter by category">
              <FilterButton
                label="All Products"
                active={active === 'all'}
                onClick={() => setCategory('all')}
              />
              {categories.map((c) => (
                <FilterButton
                  key={c.slug}
                  label={c.name}
                  active={active.toLowerCase() === c.slug.toLowerCase()}
                  onClick={() => setCategory(c.slug)}
                />
              ))}
            </div>
          )}

          {query.isPending && (
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="card">
                  <Skeleton className="h-48 w-full" />
                  <div className="space-y-3 p-6">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {query.isError && (
            <ErrorState
              title="We could not load the catalogue"
              body="This is a problem on our side. Please try again, or send us your requirements and we will quote from your list."
              onRetry={() => void query.refetch()}
            />
          )}

          {query.isSuccess && visible.length === 0 && (
            <EmptyState
              title={
                products.length === 0
                  ? 'Our catalogue is being updated'
                  : 'No products in this category yet'
              }
              body="We supply a full range of cleaning, hygiene, paper and janitorial products. Send us your requirements and we will quote directly from your list."
              action={
                <Link href="/quote" className="btn btn-primary btn-hero">
                  Request a Quote
                </Link>
              }
            />
          )}

          {query.isSuccess && visible.length > 0 && (
            <>
              <p className="sr-only" aria-live="polite">
                {visible.length} products shown
              </p>
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {visible.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}

function FilterButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-md px-5 py-2 text-sm font-semibold transition-colors ${
        active
          ? 'bg-navy text-white'
          : 'border border-border bg-white text-charcoal hover:border-navy hover:text-navy'
      }`}
    >
      {label}
    </button>
  );
}
